/**
 * OfflineErrors — lightweight structured error tracking for the offline subsystem.
 *
 * Collects errors from IndexedDB operations, sync failures, interceptor issues, etc.
 * Accessible via window.OfflineErrors for debugging in DevTools console.
 *
 * Usage:
 *   import OfflineErrors from "./OfflineErrors";
 *   OfflineErrors.log("CacheResponse", error, { key: "some_key" });
 *
 * Debug in console:
 *   window.OfflineErrors.getErrors()   // all recent errors
 *   window.OfflineErrors.clear()       // reset
 */
class OfflineErrors {
	static #errors = [];
	static #MAX_ERRORS = 100;

	/**
	 * Log a structured error entry.
	 * @param {string} context - Where the error occurred (e.g., "CacheResponse", "FilterPrecachedItems")
	 * @param {Error|string} error - The error object or message
	 * @param {object} [extra] - Additional context (keys, URLs, counts, etc.)
	 */
	static log(context, error, extra = {}) {
		const entry = {
			context,
			error: error?.message || String(error),
			timestamp: Date.now(),
			...extra,
		};
		console.warn(`[Offline:${context}]`, entry.error, extra);
		this.#errors.push(entry);
		if (this.#errors.length > this.#MAX_ERRORS) {
			this.#errors.shift();
		}
	}

	/**
	 * Get all collected errors (most recent last).
	 * @returns {Array<object>}
	 */
	static getErrors() {
		return [...this.#errors];
	}

	/**
	 * Clear all collected errors.
	 */
	static clear() {
		this.#errors = [];
	}
}

// Expose on window for DevTools debugging
if (typeof window !== "undefined") {
	window.OfflineErrors = OfflineErrors;
}

export default OfflineErrors;
